import { DirectReportDto } from "@coaching-culture/types";
import { EmptyState, Flex, Text, typeSize } from "@coaching-culture/ui";
import { min, range } from "lodash";
import { mix } from "polished";
import { FaChartBar } from "react-icons/fa";
import styled, { useTheme } from "styled-components";

const Track = styled.div`
  width: 100%;
  height: 16px;
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.grey4};
  margin: 3px 0;
  position: relative;
`;

const Needle = styled.div`
  height: 100%;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
  font-size: ${typeSize("caption")};
  display: flex;
  align-items: center;
  transition: all 0.5s ease;
  background-color: ${(props) => props.theme.colors.grey1};
`;

export type MeasuresChartProps = {
  user: DirectReportDto;
};

export const makeRamp = (colors: string[], count: number) =>
  range(count)
    .map((x) => {
      const p = x / (count - 1);
      const i = Math.floor(p * (colors.length - 1));
      const i2 = min([i + 1, colors.length - 1]);
      const p2 = p * (colors.length - 1) - i;
      const c = mix(p2, colors[i2], colors[i]);
      return c;
    })
    .reverse();

export const MeasuresChart = ({ user }: MeasuresChartProps) => {
  const theme = useTheme();

  if (user.measures.length === 0) {
    return (
      <EmptyState icon={FaChartBar} compact mr={3}>
        No measures recorded yet.
      </EmptyState>
    );
  }

  return (
    <Flex width="100%" mr={3} flexDirection="column">
      {user.measures.map((x, i) => {
        const colorRamp = makeRamp(
          [theme.colors.danger, theme.colors.warning, theme.colors.positive],
          x.options.length,
        ).reverse();

        const index = x.options.findIndex((o) => o.id === x.value);
        const opt = x.options[index];

        const position = index / Math.max(1, x.options.length - 1);

        return (
          <Flex alignItems={"center"} key={i}>
            <Text
              mr={2}
              textAlign={"right"}
              fontWeight={500}
              fontSize="caption"
              style={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "130px",
              }}
            >
              {x.name}
            </Text>
            <Track>
              {index > -1 && (
                <Needle
                  style={{
                    width: `${25 + position * 75}%`,
                    background: colorRamp[index],
                  }}
                >
                  {opt?.name}
                </Needle>
              )}
            </Track>
          </Flex>
        );
      })}
    </Flex>
  );
};
